import { createTheme } from '@mui/material/styles'
import type { ResolvedTheme } from '@app/theme-preference'

const colors = {
  background: '#F2F2F2',
  black: '#000000',
  borderColor: 'rgb(242, 244, 247)',
  primary: '#F2CF0D',
  primaryDarker: '#F8B430',
  red: '#CF2E34',
  redDarker: '#990002',
  secondary: '#482611',
  secondaryDarker: '#391D0C',
  textColor: '#391D0C',
  white: '#FFFFFF',
} as const

export function createAppTheme(resolvedTheme: ResolvedTheme) {
  const isDark = resolvedTheme === 'dark'

  return createTheme({
  cssVariables: true,
  palette: {
    mode: resolvedTheme,
    background: {
      default: isDark ? '#0b1620' : colors.background,
      paper: isDark ? '#132433' : colors.white,
    },
    error: {
      main: colors.red,
    },
    primary: {
      main: colors.primary,
      dark: colors.primaryDarker,
      contrastText: colors.textColor,
    },
    secondary: {
      main: colors.secondary,
      dark: colors.secondaryDarker,
      contrastText: colors.white,
    },
    text: {
      primary: isDark ? '#f3f7fa' : colors.black,
      secondary: isDark ? '#b5c3cf' : '#646777',
    },
  },
  shape: {
    borderRadius: 10,
  },
  typography: {
    button: {
      fontSize: 16,
      fontWeight: 600,
      textTransform: 'none',
    },
    fontFamily:
      'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", "Noto Sans", Arial, sans-serif',
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        colorDefault: {
          backgroundColor: isDark ? '#132433' : colors.white,
          color: colors.primary,
        },
        colorPrimary: {
          backgroundColor: colors.primary,
          color: colors.white,
        },
        root: {
          boxShadow: '0 0.4375rem 0.5625rem -0.4375rem #d6dee6',
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: isDark ? 'rgb(2 8 14 / 0.72)' : 'rgb(255 255 255 / 0.5)',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        contained: {
          '&:active, &:focus, &:hover': {
            opacity: 0.92,
          },
          backgroundColor: isDark ? '#132433' : colors.white,
          color: isDark ? '#f3f7fa' : colors.black,
        },
        outlined: {
          borderWidth: 1,
          padding: '.75rem 2.5rem',
        },
        root: {
          '&.MuiButton-containedPrimary': {
            '&:active, &:focus, &:hover': {
              backgroundColor: colors.primaryDarker,
              color: colors.textColor,
            },
            backgroundColor: colors.primary,
            color: colors.textColor,
          },
          '&.MuiButton-containedSecondary': {
            '&:active, &:focus, &:hover': {
              backgroundColor: colors.secondaryDarker,
              color: colors.white,
            },
            backgroundColor: colors.secondary,
            color: colors.white,
          },
          '&.MuiButton-outlinedPrimary': {
            '&:active, &:focus, &:hover': {
              backgroundColor: colors.primary,
              borderColor: colors.primary,
              color: colors.white,
            },
            borderColor: colors.primary,
            color: colors.primary,
          },
          '&.MuiButton-outlinedSecondary': {
            '&:active, &:focus, &:hover': {
              backgroundColor: colors.secondary,
              borderColor: colors.secondary,
              color: colors.white,
            },
            borderColor: colors.secondary,
            color: colors.secondary,
          },
          borderRadius: '.5rem',
          padding: '.75rem 2.5rem',
        },
        text: {
          '&:hover': {
            backgroundColor: 'rgb(245 245 245)',
          },
          borderRadius: 4,
          color: isDark ? '#f3f7fa' : colors.black,
          padding: '.75rem 2.5rem',
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          paddingLeft: 0,
          paddingRight: 0,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paperFullScreen: {
          background: isDark ? '#0b1620' : colors.background,
          border: 0,
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          bottom: 16,
          boxShadow: '0 0.1875rem 0.3125rem 0 rgb(0 0 0 / 0.1)',
          position: 'absolute',
          right: 16,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        colorSecondary: {
          '&:hover': {
            color: '#863E15',
          },
          color: colors.secondary,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          '& svg': {
            color: colors.primary,
          },
          '&:hover': {
            backgroundColor: isDark ? '#1b3040' : 'rgb(250 250 250)',
          },
          borderBottom: '1px solid rgb(245 245 245)',
          borderRadius: 8,
          color: isDark ? '#f3f7fa' : colors.black,
          fontSize: 16,
          fontWeight: 700,
          padding: '.75rem 2.5rem',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        elevation1: {
          boxShadow: 'rgb(7 7 63 / 0.08) 0 1px 9px 0',
        },
        elevation16: {
          boxShadow: 'rgb(7 7 63 / 0.08) 0 1px 9px 0',
        },
        elevation24: {
          border: `1px solid ${colors.borderColor}`,
          boxShadow: 'none',
        },
        elevation4: {
          boxShadow: 'rgb(7 7 63 / 0.08) 0 1px 9px 0',
        },
        elevation8: {
          boxShadow: 'rgb(7 7 63 / 0.08) 0 1px 9px 0',
        },
        rounded: {
          borderRadius: 8,
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          borderRadius: 8,
          marginTop: '1.25rem',
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          boxShadow: 'rgb(7 7 63 / 0.08) 0 1px 9px 0',
        },
      },
    },
  },
  })
}
